import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const FREE_TIER_SHIFT_LIMIT = 10;
const FREE_TIER_PATIENT_LIMIT = 1;

interface SubscriptionRequiredResult {
  isRequired: boolean;
  isLoading: boolean;
  isFetching: boolean;
  shiftsCount: number;
  shiftsLimit: number;
  patientsCount: number;
  patientsLimit: number;
  suspendedCount: number;
  canCreatePatient: boolean;
  isOwner: boolean;
}

/**
 * Hook to determine if subscription is required for the current user.
 * 
 * Rules:
 * - Only owners need subscription (caregivers and observers are free)
 * - Owners get 10 free shifts across all their patients
 * - After 10 shifts, subscription is required
 * - Patient limit: free tier = 1, subscribed = quantity from Stripe
 */
export function useSubscriptionRequired(): SubscriptionRequiredResult {
  const { user, isLoggingOut, subscription } = useAuth();

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['subscription-required', user?.id],
    queryFn: async () => {
      if (!user?.id || isLoggingOut) {
        console.log('[useSubscriptionRequired] No user ID or logging out, returning default values');
        return { isOwner: false, shiftsCount: 0, patientsCount: 0 };
      }

      const { data: ownerLinks, error: linksError } = await supabase
        .from('patient_user_links')
        .select('patient_id, patients!inner(status)')
        .eq('user_id', user.id)
        .eq('role', 'owner')
        .eq('active', true)
        .in('patients.status', ['active', 'suspended']);

      if (linksError) {
        console.error('[useSubscriptionRequired] Error fetching owner links:', linksError);
        throw linksError;
      }

      if (!ownerLinks || ownerLinks.length === 0) {
        return { isOwner: false, shiftsCount: 0, patientsCount: 0, suspendedCount: 0 };
      }

      const activeLinks = ownerLinks.filter((l: any) => l.patients?.status === 'active');
      const suspendedLinks = ownerLinks.filter((l: any) => l.patients?.status === 'suspended');
      const patientIds = activeLinks.map((link: any) => link.patient_id);
      const patientsCount = patientIds.length;
      const suspendedCount = suspendedLinks.length;

      if (patientIds.length === 0) {
        return { isOwner: true, shiftsCount: 0, patientsCount: 0, suspendedCount };
      }

      const { count, error: shiftsError } = await supabase
        .from('shifts')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'completed')
        .in('patient_id', patientIds);

      if (shiftsError) {
        console.error('[useSubscriptionRequired] Error counting shifts:', shiftsError);
        throw shiftsError;
      }

      return { isOwner: true, shiftsCount: count || 0, patientsCount, suspendedCount };
    },
    enabled: !!user && !isLoggingOut,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Resync when the tab regains focus, so post-inactivity decisions use fresh counts.
    refetchOnWindowFocus: true,
    // On transient errors, don't hammer Stripe-adjacent flows; one quick retry is enough.
    retry: 1,
  });

  const isOwner = data?.isOwner ?? false;
  const shiftsCount = data?.shiftsCount ?? 0;
  const patientsCount = data?.patientsCount ?? 0;
  const suspendedCount = data?.suspendedCount ?? 0;

  const isRequired =
    !isLoggingOut &&
    !isError &&
    !subscription.subscribed &&
    isOwner &&
    shiftsCount >= FREE_TIER_SHIFT_LIMIT;

  const patientsLimit = subscription.subscribed && subscription.patientSlots
    ? subscription.patientSlots
    : FREE_TIER_PATIENT_LIMIT;

  const canCreatePatient = !isOwner || patientsCount < patientsLimit;

  return {
    isRequired,
    isLoading,
    isFetching,
    shiftsCount,
    shiftsLimit: FREE_TIER_SHIFT_LIMIT,
    patientsCount,
    patientsLimit,
    suspendedCount,
    canCreatePatient,
    isOwner,
  };
}
